import React, { useEffect, useState } from 'react';
import { adminDashboardAPI, adminCompanyAPI, adminUpgradeAPI } from '../api';

const PLAN_COLOR = { pro: '#8b5cf6', enterprise: '#0ea5e9', starter: '#3b82f6' };
const STATUS_COLOR = { paid: '#166534', cash: '#1e3a5f', pending: '#713f12', overdue: '#7f1d1d' };
const STATUS_TEXT  = { paid: '#4ade80', cash: '#60a5fa', pending: '#fbbf24', overdue: '#f87171' };

// Pricing comes from GET /api/admin/dashboard/config (single source of truth).
// Shape is tier-keyed then currency-keyed: { pro: { BWP, USD }, enterprise: {…} }.
// This fallback is only used if that request hasn't resolved or fails.
const FALLBACK_PRICING = {
  starter:    { BWP: { monthly: 50,  annual: 550  }, USD: { monthly: 3.99,  annual: 43.89  } },
  pro:        { BWP: { monthly: 150, annual: 1650 }, USD: { monthly: 9.99,  annual: 109.89 } },
  enterprise: { BWP: { monthly: 250, annual: 2750 }, USD: { monthly: 14.99, annual: 164.89 } },
};

// Mirrors the backend trial policy: 14 days; starter on any cycle, pro/enterprise
// only on annual billing.
const trialEligible = (plan, cycle) => (plan === 'starter' ? true : cycle === 'annual');
const CURRENCY_SYMBOL  = { BWP: 'P', USD: '$' };

// Botswana companies are billed in Pula; everyone else in USD.
const billingCurrency = (currency) => (currency || '').trim() === 'BWP' ? 'BWP' : 'USD';
// Resolve the {monthly, annual} table for a given tier + currency.
const priceTableFor   = (pricing, plan, currency) => {
  const p    = pricing || FALLBACK_PRICING;
  const tier = p[plan] || FALLBACK_PRICING[plan] || {};
  return tier[billingCurrency(currency)] || tier.USD || { monthly: 0, annual: 0 };
};

const PAYMENT_METHOD_OPTS = [
  { value: 'orange_money',  label: 'Orange Money' },
  { value: 'myzaka',        label: 'Myzaka (Mascom)' },
  { value: 'smega',         label: 'Smega (BTC)' },
  { value: 'eft',           label: 'EFT / bank transfer' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join('/');
};
const fmtAgo  = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30)  return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

function PaymentBadge({ status }) {
  if (!status) return null;
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 4,
      background: STATUS_COLOR[status] || '#1e293b', color: STATUS_TEXT[status] || '#94a3b8',
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{status}</span>
  );
}

function ContactRow({ name, email, phone, whatsapp, role, active, last_login_at }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
      padding: '0.5rem 0', borderBottom: '1px solid #1e293b',
      opacity: active === false ? 0.45 : 1,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0, marginTop: 2,
        background: role === 'owner' ? '#0f4c81' : role === 'manager' ? '#4c0f81' : '#1e293b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8',
      }}>
        {(name || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#e2e8f0' }}>
          {name} {active === false && <span style={{ color: '#ef4444', fontWeight: 400, fontSize: '0.72rem' }}>· inactive</span>}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{email}</div>
        {(phone || whatsapp) && (
          <div style={{ fontSize: '0.73rem', color: '#64748b' }}>
            {phone && <span>📞 {phone}</span>}
            {phone && whatsapp && <span style={{ margin: '0 0.3rem' }}>·</span>}
            {whatsapp && <span>WhatsApp: {whatsapp}</span>}
          </div>
        )}
        {last_login_at && (
          <div style={{ fontSize: '0.7rem', color: '#475569' }}>Last login: {fmtAgo(last_login_at)}</div>
        )}
      </div>
      <span style={{
        fontSize: '0.66rem', fontWeight: 600, padding: '0.15rem 0.45rem', borderRadius: 4,
        background: role === 'owner' ? '#1e3a5f' : role === 'manager' ? '#2d1b4e' : '#1e293b',
        color: role === 'owner' ? '#60a5fa' : role === 'manager' ? '#c084fc' : '#94a3b8',
        textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0, marginTop: 3,
      }}>
        {role}
      </span>
    </div>
  );
}

function ToggleBtn({ active, onToggle, loading }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onToggle(); }}
      disabled={loading}
      style={{
        padding: '0.25rem 0.65rem', borderRadius: 20, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
        fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
        background: active ? '#166534' : '#7f1d1d',
        color: active ? '#4ade80' : '#fca5a5',
        opacity: loading ? 0.6 : 1, flexShrink: 0,
      }}
    >
      {loading ? '…' : active ? 'ACTIVE' : 'INACTIVE'}
    </button>
  );
}

// Permanent-delete control. Only rendered for deactivated accounts; the backend
// additionally refuses if the account has any sales/purchases. Requires the
// admin to type the exact company name before the Delete button enables.
function DeleteCompanyButton({ company, onDeleted }) {
  const [open,  setOpen]  = useState(false);
  const [typed, setTyped] = useState('');
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState('');
  const match = typed.trim() === company.name;

  const close = () => { setOpen(false); setTyped(''); setError(''); };

  const handleDelete = async () => {
    if (!match || busy) return;
    setBusy(true); setError('');
    try {
      await adminCompanyAPI.remove(company.id, typed.trim());
      close();
      onDeleted && onDeleted();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); setOpen(true); }}
        style={{ background: '#1e293b', border: '1px solid #7f1d1d', color: '#f87171', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}
      >
        Delete
      </button>
      {open && (
        <div onClick={e => { e.stopPropagation(); close(); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#1e293b', border: '1px solid #7f1d1d', borderRadius: 12, padding: '1.5rem', width: 400, maxWidth: '90vw' }}>
            <h3 style={{ color: '#f87171', margin: '0 0 0.5rem' }}>Delete account permanently</h3>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 0.9rem' }}>
              This permanently removes <strong style={{ color: '#f1f5f9' }}>{company.name}</strong> and all its
              businesses and users. This cannot be undone.
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0 0 0.4rem' }}>
              Type <strong style={{ color: '#f1f5f9' }}>{company.name}</strong> to confirm:
            </p>
            <input
              value={typed} onChange={e => setTyped(e.target.value)} placeholder={company.name} autoFocus
              style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 6, background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '0.85rem', boxSizing: 'border-box' }}
            />
            {error && <div style={{ color: '#f87171', fontSize: '0.78rem', marginTop: '0.5rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.1rem' }}>
              <button onClick={close} style={{ background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: 6, padding: '0.45rem 1rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDelete} disabled={!match || busy}
                style={{ background: match ? '#dc2626' : '#7f1d1d', color: '#fff', border: 'none', borderRadius: 6, padding: '0.45rem 1rem', fontWeight: 700, cursor: match && !busy ? 'pointer' : 'not-allowed', opacity: busy ? 0.7 : 1 }}>
                {busy ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BusinessCard({ biz }) {
  const [open, setOpen] = useState(false);
  const active = biz.active;
  const totalMembers = biz.staff.length + (biz.owner ? 1 : 0);

  return (
    <div style={{
      background: '#0f172a', border: `1px solid ${active ? '#1e293b' : '#450a0a'}`,
      borderRadius: 8, overflow: 'hidden', opacity: active ? 1 : 0.7,
    }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '0.7rem 1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#e2e8f0' }}>{biz.name}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {biz.currency?.trim()}{biz.country_code ? ` · ${biz.country_code.trim()}` : ''}
            {biz.industry ? ` · ${biz.industry}` : ''}
            {biz.employee_count ? ` · ${biz.employee_count} employees` : ''}
            {' · '}{totalMembers} member{totalMembers !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '0.71rem', color: '#475569', marginTop: 2 }}>
            Last activity: {fmtAgo(biz.last_activity_at)}
            {biz.last_sync_at && <span> · Synced: {fmtAgo(biz.last_sync_at)}</span>}
            {biz.first_sale_at && <span> · First sale: {fmtDate(biz.first_sale_at)}</span>}
          </div>
        </div>
        <span style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 3 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: '0 1rem 0.75rem', borderTop: '1px solid #1e293b' }}>
          {biz.owner && (
            <ContactRow
              name={biz.owner.name} email={biz.owner.email}
              phone={biz.owner.phone} whatsapp={biz.owner.whatsapp}
              role="owner" active last_login_at={biz.owner.last_login_at}
            />
          )}
          {biz.staff.map(s => (
            <ContactRow
              key={s.id} name={s.name} email={s.email}
              phone={s.phone} whatsapp={s.whatsapp}
              role={s.role} active={s.active} last_login_at={s.last_login_at}
            />
          ))}
          {!biz.owner && biz.staff.length === 0 && (
            <p style={{ color: '#475569', fontSize: '0.78rem', padding: '0.5rem 0' }}>No users yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY_PAY = { payment_status: '', payment_method: '', subscription_start: '', subscription_expiry: '', last_payment_at: '', last_payment_amount: '', industry: '', referral_source: '' };

function PaymentPanel({ company, onSaved }) {
  const [form, setForm]   = useState({
    payment_status:      company.payment_status      || '',
    payment_method:      company.payment_method      || '',
    subscription_start:  company.subscription_start  ? new Date(company.subscription_start).toISOString().slice(0,10)  : '',
    subscription_expiry: company.subscription_expiry ? new Date(company.subscription_expiry).toISOString().slice(0,10) : '',
    last_payment_at:     company.last_payment_at     ? new Date(company.last_payment_at).toISOString().slice(0,10)     : '',
    last_payment_amount: company.last_payment_amount || '',
    industry:            company.industry            || '',
    referral_source:     company.referral_source     || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setSaving(true); setError('');
    try {
      await adminCompanyAPI.updateInfo(company.id, {
        ...form,
        subscription_start:  form.subscription_start  || null,
        subscription_expiry: form.subscription_expiry || null,
        last_payment_at:     form.last_payment_at     || null,
        last_payment_amount: form.last_payment_amount ? parseFloat(form.last_payment_amount) : null,
      });
      onSaved && onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const lbl = { fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: 3 };
  const inp = { width: '100%', padding: '0.4rem 0.6rem', borderRadius: 6, background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '0.82rem' };

  return (
    <form onSubmit={handleSave} onClick={e => e.stopPropagation()} style={{ padding: '0.9rem 1.1rem', borderTop: '1px solid #1e293b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
      <div>
        <label style={lbl}>Payment status</label>
        <select value={form.payment_status} onChange={set('payment_status')} style={inp}>
          <option value="">— select —</option>
          <option value="paid">Paid</option>
          <option value="cash">Cash</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>
      <div>
        <label style={lbl}>Payment method</label>
        <select value={form.payment_method} onChange={set('payment_method')} style={inp}>
          <option value="">— select —</option>
          {PAYMENT_METHOD_OPTS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div>
        <label style={lbl}>Subscription start</label>
        <input type="date" value={form.subscription_start} onChange={set('subscription_start')} style={inp} />
      </div>
      <div>
        <label style={lbl}>Subscription expiry</label>
        <input type="date" value={form.subscription_expiry} onChange={set('subscription_expiry')} style={inp} />
      </div>
      <div>
        <label style={lbl}>Last payment date</label>
        <input type="date" value={form.last_payment_at} onChange={set('last_payment_at')} style={inp} />
      </div>
      <div>
        <label style={lbl}>Last payment amount</label>
        <input type="number" step="any" min="0" value={form.last_payment_amount} onChange={set('last_payment_amount')} style={inp} placeholder="0.00" />
      </div>
      <div>
        <label style={lbl}>Industry</label>
        <input type="text" value={form.industry} onChange={set('industry')} style={inp} placeholder="e.g. Retail, Apiary" />
      </div>
      <div>
        <label style={lbl}>How they found us</label>
        <input type="text" value={form.referral_source} onChange={set('referral_source')} style={inp} placeholder="e.g. Word of mouth" />
      </div>
      {error && <div style={{ gridColumn: '1/-1', color: '#f87171', fontSize: '0.78rem' }}>{error}</div>}
      <div style={{ gridColumn: '1/-1' }}>
        <button type="submit" disabled={saving} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.82rem' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

const PLAN_OPTIONS = [
  { value: 'starter',    label: 'Starter' },
  { value: 'pro',        label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];
const SET_LABEL = { starter: 'Set Starter', pro: 'Set Pro', enterprise: 'Set Enterprise' };

// Recent plan_audit rows for a company — every trial, activation, renewal,
// upgrade/downgrade and auto-expiry, newest first.
function PlanHistory({ companyId, currency, refreshKey }) {
  const sym = CURRENCY_SYMBOL[billingCurrency(currency)] || '';
  const [rows, setRows] = useState(null);   // null = loading, [] = none

  useEffect(() => {
    let alive = true;
    adminCompanyAPI.planHistory(companyId)
      .then(r => { if (alive) setRows(r.data.history || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [companyId, refreshKey]);

  const ACTION_TEXT = { comp_grant: 'complimentary', comp_end: 'comp ended' };
  const pretty = (a) => ACTION_TEXT[a] || (a || 'change').replace(/_/g, ' ');
  if (rows === null) return <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Loading plan history…</div>;
  if (!rows.length)  return <div style={{ fontSize: '0.74rem', color: '#64748b' }}>No plan changes recorded yet.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
      {rows.map((h, i) => (
        <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'baseline', fontSize: '0.74rem', color: '#94a3b8', borderBottom: i < rows.length - 1 ? '1px solid #1e293b' : 'none', paddingBottom: 4 }}>
          <span style={{ color: '#64748b', minWidth: 72 }}>{fmtDate(h.created_at)}</span>
          <span style={{ color: '#c4b5fd', fontWeight: 600, textTransform: 'capitalize' }}>{pretty(h.action)}</span>
          <span>
            {h.old_plan && h.old_plan !== h.new_plan ? `${h.old_plan} → ` : ''}{h.new_plan || ''}
            {h.billing_cycle ? ` · ${h.billing_cycle}` : ''}
          </span>
          {h.amount != null && Number(h.amount) > 0 && <span style={{ color: '#4ade80' }}>{sym}{Number(h.amount).toFixed(2)}</span>}
          {h.new_expiry && <span style={{ color: '#64748b' }}>{h.action === 'comp_end' ? 'ended' : 'expires'} {fmtDate(h.new_expiry)}</span>}
          {h.action === 'comp_grant' && !h.new_expiry && <span style={{ color: '#64748b' }}>no end date</span>}
          {h.note && <span style={{ color: '#64748b', fontStyle: 'italic' }}>“{h.note}”</span>}
          {h.changed_by_email && <span style={{ color: '#475569' }}>by {h.changed_by_email}</span>}
        </div>
      ))}
    </div>
  );
}

function PlanPanel({ company, pricing, onSaved }) {
  const sym = CURRENCY_SYMBOL[billingCurrency(company.currency)] || '';

  const [plan,    setPlan]    = useState(company.plan || 'starter');
  const [cycle,   setCycle]   = useState(company.billing_cycle || 'monthly');
  const [method,  setMethod]  = useState(company.payment_method || '');
  const [touched, setTouched] = useState(false); // did the admin override the suggested amount?
  const [demote,  setDemote]  = useState(false); // starter only: drop the plan without recording a payment
  const [trial,   setTrial]   = useState(false); // activate a 14-day free trial instead of a payment
  const [note,    setNote]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [dupBlocked, setDupBlocked] = useState(false); // backend flagged a likely duplicate — offer a force retry
  const [histKey, setHistKey] = useState(0); // bump to reload plan history after a save

  // All three tiers are paid now; "demote" (starter only) is the unpaid path.
  const isPaid   = !(plan === 'starter' && demote);
  const table    = priceTableFor(pricing, plan, company.currency);
  // Show the trial control whenever policy allows it for this plan/cycle. It is
  // NO LONGER hidden once a trial was used — admins can re-grant a 14-day trial
  // "if there is a need"; the re-grant sends force:true to override the once-only rule.
  const canTrial   = isPaid && trialEligible(plan, cycle);
  const isRegrant  = trial && !!company.trial_used;

  const [amount, setAmount] = useState(String(table[company.billing_cycle || 'monthly']));

  const changePlan = (e) => {
    const p = e.target.value;
    setPlan(p);
    if (p !== 'starter') setDemote(false);
    if (!trialEligible(p, cycle)) setTrial(false);
    if (!touched) {
      const t = priceTableFor(pricing, p, company.currency);
      setAmount(String(t[cycle]));
    }
  };

  const changeCycle = (e) => {
    const c = e.target.value;
    setCycle(c);
    if (!trialEligible(plan, c)) setTrial(false);
    if (!touched) setAmount(String(table[c])); // keep amount in step with cycle until overridden
  };

  const handleSave = async (e, forceOverride = false) => {
    e.preventDefault(); e.stopPropagation();
    setSaving(true); setError('');
    // force is sent when re-granting a used trial, or when the admin confirms a
    // duplicate-flagged paid activation via the "Activate anyway" button.
    const force = forceOverride || isRegrant || undefined;
    try {
      await adminCompanyAPI.setPlan(company.id, {
        plan,
        billing_cycle:  isPaid ? cycle : null,
        payment_method: isPaid && !trial ? (method || null) : null,
        amount:         isPaid && !trial ? parseFloat(amount) : null,
        trial:          isPaid && trial ? true : undefined,
        force,
        note:           note || null,
      });
      setDupBlocked(false);
      setHistKey(k => k + 1);
      onSaved && onSaved();
    } catch (err) {
      if (err.response?.data?.duplicate) setDupBlocked(true);
      setError(err.response?.data?.error || 'Failed to set plan');
    } finally { setSaving(false); }
  };

  const lbl = { fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: 3 };
  const inp = { width: '100%', padding: '0.4rem 0.6rem', borderRadius: 6, background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '0.82rem' };

  return (
    <form onSubmit={handleSave} onClick={e => e.stopPropagation()} style={{ padding: '0.9rem 1.1rem', borderTop: '1px solid #334155', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', background: '#0b1120' }}>
      <div style={{ gridColumn: '1/-1', fontSize: '0.72rem', fontWeight: 700, color: '#a78bfa', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Manage plan</div>
      <div>
        <label style={lbl}>Plan</label>
        <select value={plan} onChange={changePlan} style={inp}>
          {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {isPaid && (
        <div>
          <label style={lbl}>Billing cycle</label>
          <select value={cycle} onChange={changeCycle} style={inp}>
            <option value="monthly">Monthly — {sym}{table.monthly}</option>
            <option value="annual">Annual — {sym}{table.annual}</option>
          </select>
        </div>
      )}
      {plan === 'starter' && (
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, cursor: 'pointer' }}>
            <input type="checkbox" checked={demote} onChange={e => { setDemote(e.target.checked); if (e.target.checked) setTrial(false); }} />
            Demote without payment (drops any billing cycle; no subscription recorded)
          </label>
        </div>
      )}
      {canTrial && (
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, cursor: 'pointer', color: isRegrant ? '#fbbf24' : '#86efac' }}>
            <input type="checkbox" checked={trial} onChange={e => setTrial(e.target.checked)} />
            {company.trial_used
              ? 'Grant another 14-day trial — overrides the one-per-company rule'
              : `Start 14-day free trial — no payment now${plan !== 'starter' ? ' (annual billing only)' : ''}`}
          </label>
        </div>
      )}
      {isPaid && !trial && (
        <div>
          <label style={lbl}>Payment method</label>
          <select value={method} onChange={e => setMethod(e.target.value)} style={inp}>
            <option value="">— select —</option>
            {PAYMENT_METHOD_OPTS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      )}
      {isPaid && !trial && (
        <div>
          <label style={lbl}>Amount paid ({sym})</label>
          <input type="number" step="any" min="0" value={amount}
            onChange={e => { setTouched(true); setAmount(e.target.value); }} style={inp} />
        </div>
      )}
      <div style={{ gridColumn: '1/-1' }}>
        <label style={lbl}>Note (optional)</label>
        <input type="text" value={note} onChange={e => setNote(e.target.value)} style={inp} placeholder="e.g. EFT ref 1234, paid via Orange Money" />
      </div>
      {isPaid && !trial && (
        <div style={{ gridColumn: '1/-1', fontSize: '0.71rem', color: '#64748b' }}>
          Saving extends the subscription by one {cycle === 'annual' ? 'year' : 'month'} from the later of today or the current expiry, marks it paid, and logs the change.
        </div>
      )}
      {isPaid && trial && (
        <div style={{ gridColumn: '1/-1', fontSize: '0.71rem', color: '#64748b' }}>
          Saving activates the plan for 14 days with nothing collected. Record the real payment here once it arrives — it extends from the trial's end. Each company gets one trial.
        </div>
      )}
      {error && <div style={{ gridColumn: '1/-1', color: dupBlocked ? '#fbbf24' : '#f87171', fontSize: '0.78rem' }}>{error}</div>}
      <div style={{ gridColumn: '1/-1', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
        <button type="submit" disabled={saving} style={{ background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.82rem' }}>
          {saving ? 'Saving…' : SET_LABEL[plan]}
        </button>
        {dupBlocked && (
          <button type="button" disabled={saving} onClick={e => handleSave(e, true)}
            style={{ background: 'transparent', color: '#fbbf24', border: '1px solid #fbbf24', borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}>
            Activate anyway
          </button>
        )}
      </div>
      <div style={{ gridColumn: '1/-1', borderTop: '1px solid #1e293b', paddingTop: '0.6rem', marginTop: '0.2rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#a78bfa', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>Plan history</div>
        <PlanHistory companyId={company.id} currency={company.currency} refreshKey={histKey} />
      </div>
    </form>
  );
}

// Complimentary (free) access. While a comp runs the account is never billed,
// marked overdue, made read-only or auto-demoted. Ending it resumes normal
// billing from today with the usual grace period.
const COMP_COLOR = '#f472b6';

function CompBadge({ company, small }) {
  if (!company.complimentary) return null;
  return (
    <span title={company.complimentary_reason || ''} style={{
      fontSize: small ? '0.63rem' : '0.65rem', fontWeight: 700, padding: small ? '0.12rem 0.4rem' : '0.15rem 0.5rem', borderRadius: 4,
      background: COMP_COLOR + '22', color: COMP_COLOR, textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      Comp{company.complimentary_until ? ` · to ${fmtDate(company.complimentary_until)}` : ''}
    </span>
  );
}

// A date input's YYYY-MM-DD → the end of that day in the admin's timezone.
const endOfDayISO = (d) => (d ? new Date(`${d}T23:59:59`).toISOString() : null);
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

function CompPanel({ company, onSaved }) {
  const active = !!company.complimentary;
  const [plan,    setPlan]    = useState(company.plan || 'starter');
  const [noEnd,   setNoEnd]   = useState(active ? !company.complimentary_until : true);
  const [until,   setUntil]   = useState(toDateInput(company.complimentary_until));
  const [reason,  setReason]  = useState(active ? company.complimentary_reason || '' : '');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const send = async (data) => {
    setSaving(true); setError('');
    try {
      await adminCompanyAPI.setComplimentary(company.id, data);
      onSaved && onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update complimentary access');
    } finally { setSaving(false); }
  };

  const handleGrant = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!reason.trim()) return setError('Give a reason — it is kept in the plan history.');
    if (!noEnd && !until) return setError('Pick an end date, or tick "No end date".');
    send({ complimentary: true, plan, until: noEnd ? null : endOfDayISO(until), reason: reason.trim() });
  };

  const handleEnd = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`End complimentary access for ${company.name}? Billing resumes today, with the usual grace period before the account goes read-only.`)) return;
    send({ complimentary: false });
  };

  const lbl = { fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: 3 };
  const inp = { width: '100%', padding: '0.4rem 0.6rem', borderRadius: 6, background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '0.82rem' };
  const today = toDateInput(new Date().toISOString());

  return (
    <form onSubmit={handleGrant} onClick={e => e.stopPropagation()} style={{ padding: '0.9rem 1.1rem', borderTop: '1px solid #334155', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', background: '#0b1120' }}>
      <div style={{ gridColumn: '1/-1', fontSize: '0.72rem', fontWeight: 700, color: COMP_COLOR, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Complimentary access</div>
      <div style={{ gridColumn: '1/-1', fontSize: '0.76rem', color: '#94a3b8', lineHeight: 1.5 }}>
        {active ? (
          <>Complimentary since {fmtDate(company.complimentary_since)}
            {company.complimentary_until ? `, until ${fmtDate(company.complimentary_until)}` : ', with no end date'}
            {company.complimentary_by ? ` — granted by ${company.complimentary_by}` : ''}.</>
        ) : (
          <>The account keeps the chosen plan free of charge — no payment banners, never read-only or auto-demoted.
            {company.complimentary_ended_at ? ` A previous comp ended ${fmtDate(company.complimentary_ended_at)}.` : ''}</>
        )}
      </div>
      <div>
        <label style={lbl}>Plan</label>
        <select value={plan} onChange={e => setPlan(e.target.value)} style={inp}>
          {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div>
        <label style={lbl}>Ends</label>
        <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, color: '#cbd5e1', marginBottom: 4 }}>
          <input type="checkbox" checked={noEnd} onChange={e => setNoEnd(e.target.checked)} /> No end date
        </label>
        {!noEnd && <input type="date" min={today} value={until} onChange={e => setUntil(e.target.value)} style={inp} />}
      </div>
      <div style={{ gridColumn: '1/-1' }}>
        <label style={lbl}>Reason (kept in plan history)</label>
        <input type="text" maxLength={500} value={reason} onChange={e => setReason(e.target.value)} style={inp} placeholder="e.g. Pilot partner, staff account, sponsorship" />
      </div>
      {error && <div style={{ gridColumn: '1/-1', color: '#f87171', fontSize: '0.78rem' }}>{error}</div>}
      <div style={{ gridColumn: '1/-1', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button type="submit" disabled={saving} style={{ background: COMP_COLOR, color: '#0f172a', border: 'none', borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.82rem' }}>
          {saving ? 'Saving…' : active ? 'Update comp' : 'Make complimentary'}
        </button>
        {active && (
          <button type="button" disabled={saving} onClick={handleEnd}
            style={{ background: 'transparent', color: '#f87171', border: '1px solid #f87171', borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}>
            End complimentary access
          </button>
        )}
      </div>
    </form>
  );
}

function CompanyCluster({ company, pricing, onRefresh }) {
  const [active,   setActive]   = useState(company.active);
  const [toggling, setToggling] = useState(false);
  const [open,     setOpen]     = useState(false);
  const [editPay,  setEditPay]  = useState(false);
  const [editPlan, setEditPlan] = useState(false);
  const [editComp, setEditComp] = useState(false);
  const bizCount  = company.businesses.length;
  const planColor = PLAN_COLOR[company.plan] || '#64748b';

  const isExpired = !company.complimentary && company.subscription_expiry && new Date(company.subscription_expiry) < new Date();

  const handleToggle = async () => {
    setToggling(true);
    try {
      if (active) await adminCompanyAPI.deactivate(company.id);
      else        await adminCompanyAPI.activate(company.id);
      setActive(a => !a);
      onRefresh && onRefresh();
    } catch {}
    finally { setToggling(false); }
  };

  return (
    <div style={{
      background: '#1e293b', border: `1px solid ${active ? '#334155' : '#450a0a'}`,
      borderRadius: 10, marginBottom: '1.25rem', overflow: 'hidden', opacity: active ? 1 : 0.75,
    }}>
      <div style={{ padding: '0.85rem 1.1rem', borderBottom: open ? '1px solid #334155' : 'none', display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#f1f5f9' }}>{company.name}</span>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 4, background: planColor + '22', color: planColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{company.plan}</span>
            {company.complimentary ? <CompBadge company={company} /> : <PaymentBadge status={company.payment_status} />}
            {isExpired && <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 4, background: '#7f1d1d', color: '#f87171', textTransform: 'uppercase' }}>EXPIRED</span>}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
            {bizCount} business{bizCount !== 1 ? 'es' : ''} · {company.currency?.trim()}
            {company.industry ? ` · ${company.industry}` : ''}
          </div>
          <div style={{ fontSize: '0.71rem', color: '#475569', marginTop: 2 }}>
            {company.subscription_expiry && <span style={{ color: isExpired ? '#f87171' : '#475569' }}>Expires: {fmtDate(company.subscription_expiry)}</span>}
            {company.last_payment_at && <span style={{ marginLeft: '0.5rem' }}>· Last paid: {fmtAgo(company.last_payment_at)}</span>}
            {company.referral_source && <span style={{ marginLeft: '0.5rem' }}>· Via: {company.referral_source}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); setEditPlan(p => !p); setEditPay(false); setEditComp(false); }} style={{ background: '#1e293b', border: '1px solid #6d28d9', color: '#a78bfa', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}>
            {editPlan ? 'Close' : 'Plan'}
          </button>
          <button onClick={e => { e.stopPropagation(); setEditComp(p => !p); setEditPlan(false); setEditPay(false); }} style={{ background: '#1e293b', border: `1px solid ${COMP_COLOR}`, color: COMP_COLOR, borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}>
            {editComp ? 'Close' : 'Comp'}
          </button>
          <button onClick={e => { e.stopPropagation(); setEditPay(p => !p); setEditPlan(false); setEditComp(false); }} style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}>
            {editPay ? 'Close' : 'Edit info'}
          </button>
          <ToggleBtn active={active} onToggle={handleToggle} loading={toggling} />
          {!active && <DeleteCompanyButton company={company} onDeleted={onRefresh} />}
          <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {editPlan && <PlanPanel company={company} pricing={pricing} onSaved={() => { setEditPlan(false); onRefresh(); }} />}
      {editComp && <CompPanel company={company} onSaved={() => { setEditComp(false); onRefresh(); }} />}
      {editPay && <PaymentPanel company={company} onSaved={() => { setEditPay(false); onRefresh(); }} />}

      {open && (
        <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {company.businesses.map(b => (
            <BusinessCard key={b.id} biz={{ ...b, active: active ? b.active : false }} />
          ))}
        </div>
      )}
    </div>
  );
}

function StandaloneCard({ company, pricing, onRefresh }) {
  const [active,   setActive]   = useState(company.active);
  const [toggling, setToggling] = useState(false);
  const [editPay,  setEditPay]  = useState(false);
  const [editPlan, setEditPlan] = useState(false);
  const [editComp, setEditComp] = useState(false);
  const planColor = PLAN_COLOR[company.plan] || '#64748b';
  const isExpired = !company.complimentary && company.subscription_expiry && new Date(company.subscription_expiry) < new Date();

  const handleToggle = async () => {
    setToggling(true);
    try {
      if (active) await adminCompanyAPI.deactivate(company.id);
      else        await adminCompanyAPI.activate(company.id);
      setActive(a => !a);
      onRefresh && onRefresh();
    } catch {}
    finally { setToggling(false); }
  };

  return (
    <div style={{ background: '#1e293b', border: `1px solid ${active ? '#334155' : '#450a0a'}`, borderRadius: 8, overflow: 'hidden', opacity: active ? 1 : 0.7 }}>
      <div style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#f1f5f9' }}>{company.name}</span>
            <span style={{ fontSize: '0.63rem', fontWeight: 700, padding: '0.12rem 0.4rem', borderRadius: 4, background: planColor + '22', color: planColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{company.plan}</span>
            {company.complimentary ? <CompBadge company={company} small /> : <PaymentBadge status={company.payment_status} />}
            {isExpired && <span style={{ fontSize: '0.63rem', fontWeight: 700, padding: '0.12rem 0.4rem', borderRadius: 4, background: '#7f1d1d', color: '#f87171', textTransform: 'uppercase' }}>EXPIRED</span>}
          </div>
          <div style={{ fontSize: '0.73rem', color: '#64748b', marginTop: 2 }}>
            {company.currency?.trim()}
            {company.industry ? ` · ${company.industry}` : ''}
          </div>
          {(company.subscription_expiry || company.last_payment_at) && (
            <div style={{ fontSize: '0.71rem', color: '#475569', marginTop: 2 }}>
              {company.subscription_expiry && <span style={{ color: isExpired ? '#f87171' : '#475569' }}>Expires: {fmtDate(company.subscription_expiry)}</span>}
              {company.last_payment_at && <span style={{ marginLeft: '0.5rem' }}>· Last paid: {fmtAgo(company.last_payment_at)}</span>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={() => { setEditPlan(p => !p); setEditPay(false); setEditComp(false); }} style={{ background: '#0f172a', border: '1px solid #6d28d9', color: '#a78bfa', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}>
            {editPlan ? 'Close' : 'Plan'}
          </button>
          <button onClick={() => { setEditComp(p => !p); setEditPlan(false); setEditPay(false); }} style={{ background: '#0f172a', border: `1px solid ${COMP_COLOR}`, color: COMP_COLOR, borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}>
            {editComp ? 'Close' : 'Comp'}
          </button>
          <button onClick={() => { setEditPay(p => !p); setEditPlan(false); setEditComp(false); }} style={{ background: '#0f172a', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer' }}>
            {editPay ? 'Close' : 'Edit info'}
          </button>
          <ToggleBtn active={active} onToggle={handleToggle} loading={toggling} />
          {!active && <DeleteCompanyButton company={company} onDeleted={onRefresh} />}
        </div>
      </div>
      {editPlan && <PlanPanel company={company} pricing={pricing} onSaved={() => { setEditPlan(false); onRefresh(); }} />}
      {editComp && <CompPanel company={company} onSaved={() => { setEditComp(false); onRefresh(); }} />}
      {editPay && <PaymentPanel company={company} onSaved={() => { setEditPay(false); onRefresh(); }} />}
    </div>
  );
}

const PLAN_LABEL = { starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' };
const CYCLE_LABEL = { monthly: 'Monthly', annual: 'Annual' };
const REQUEST_PLAN_COLOR = { starter: '#64748b', pro: '#6366f1', enterprise: '#0ea5e9' };
// Same reference the owner sees under "How to pay" in the app.
const payRef = (companyId) => `SB-${String(companyId || '').replace(/-/g, '').slice(0, 8).toUpperCase()}`;

function UpgradeRequestsPanel() {
  const [requests,  setRequests]  = useState([]);
  const [actioning, setActioning] = useState(null);
  const [reject,    setReject]    = useState(null);
  // per-request state stored as {[requestId]: {method:'', amount:''}}
  const [fields,    setFields]    = useState({});

  const field = (id, key) => fields[id]?.[key] ?? '';
  const setField = (id, key, val) => setFields(f => ({ ...f, [id]: { ...f[id], [key]: val } }));

  const load = () => adminUpgradeAPI.list('pending').then(r => setRequests(r.data.requests || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleApprove = async (r) => {
    setActioning(r.id);
    try {
      const amt = field(r.id, 'amount');
      await adminUpgradeAPI.approve(r.id, {
        payment_method: field(r.id, 'method') || undefined,
        amount: amt !== '' ? Number(amt) : undefined,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Approve failed');
    } finally { setActioning(null); }
  };

  const handleReject = async () => {
    if (!reject) return;
    setActioning(reject.id);
    try {
      await adminUpgradeAPI.reject(reject.id, { note: reject.note });
      setReject(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Reject failed');
    } finally { setActioning(null); }
  };

  if (requests.length === 0) return null;

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ fontSize: '0.71rem', fontWeight: 700, color: '#f59e0b', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
        ⏳ Pending Payment &amp; Upgrade Requests ({requests.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {requests.map(r => {
          const hasProration = r.unused_credit != null && Number(r.unused_credit) > 0;
          const quotedAmt    = r.quoted_amount  != null ? Number(r.quoted_amount)  : null;
          const cur          = (r.currency || 'USD').trim();
          const amtPlaceholder = quotedAmt != null ? `${cur} ${Number(quotedAmt).toFixed(2)} (quoted)` : 'Amount…';
          return (
            <div key={r.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 700, color: '#f1f5f9' }}>{r.company_name}</span>
                    <span style={{ background: '#0f172a', color: '#64748b', fontSize: '0.72rem', padding: '1px 6px', borderRadius: '4px' }}>
                      {r.kind === 'renewal' ? 'Pay / renew' : `${r.current_plan} →`}
                    </span>
                    <span style={{ background: REQUEST_PLAN_COLOR[r.requested_plan] || '#6366f1', color: '#fff', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>
                      {PLAN_LABEL[r.requested_plan] || r.requested_plan}
                    </span>
                    <span style={{ background: '#1e3a5f', color: '#93c5fd', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '4px' }}>
                      {CYCLE_LABEL[r.billing_cycle]}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.83rem', color: '#94a3b8' }}>
                    {r.owner_name} · {r.owner_email
                      ? <a href={`mailto:${r.owner_email}`} style={{ color: '#60a5fa' }}>{r.owner_email}</a>
                      : <span>no email (phone account — no receipt email)</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: '0.2rem' }}>
                    Requested {fmtDate(r.requested_at)} · Payment reference <strong style={{ color: '#a78bfa' }}>{payRef(r.company_id)}</strong>
                  </div>

                  {/* Proration breakdown for admin */}
                  {quotedAmt != null && (
                    <div style={{ marginTop: '0.5rem', background: '#0f172a', borderRadius: '6px', padding: '0.5rem 0.75rem', display: 'inline-flex', flexDirection: 'column', gap: '0.15rem' }}>
                      {hasProration && (
                        <span style={{ fontSize: '0.75rem', color: '#6ee7b7' }}>
                          − {cur} {Number(r.unused_credit).toFixed(2)} unused credit applied
                        </span>
                      )}
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#a78bfa' }}>
                        Collect: {cur} {quotedAmt.toFixed(2)}
                        {hasProration && <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.75rem' }}> (prorated)</span>}
                      </span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0, minWidth: 160 }}>
                  <select
                    value={field(r.id, 'method')}
                    onChange={e => setField(r.id, 'method', e.target.value)}
                    style={{ background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: '6px', padding: '0.35rem 0.6rem', fontSize: '0.82rem' }}
                  >
                    <option value="">Payment method…</option>
                    {['orange_money','myzaka','smega','eft','bank_transfer','mobile_money','cash','card'].map(m => (
                      <option key={m} value={m}>{m.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={amtPlaceholder}
                    value={field(r.id, 'amount')}
                    onChange={e => setField(r.id, 'amount', e.target.value)}
                    style={{ background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155', borderRadius: '6px', padding: '0.35rem 0.6rem', fontSize: '0.82rem', width: '100%', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button
                      onClick={() => handleApprove(r)}
                      disabled={actioning === r.id}
                      style={{ flex: 1, background: '#22c55e', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.4rem 0.6rem', fontWeight: 700, fontSize: '0.83rem', cursor: 'pointer', opacity: actioning === r.id ? 0.6 : 1 }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => setReject({ id: r.id, note: '' })}
                      disabled={actioning === r.id}
                      style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.4rem 0.6rem', fontWeight: 700, fontSize: '0.83rem', cursor: 'pointer', opacity: actioning === r.id ? 0.6 : 1 }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reject modal */}
      {reject && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1e293b', borderRadius: '1rem', padding: '1.75rem', width: 360, border: '1px solid #334155' }}>
            <h3 style={{ color: '#f1f5f9', margin: '0 0 0.75rem' }}>Reject request</h3>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 1rem' }}>Optionally add a reason to include in the email sent to the user.</p>
            <textarea
              value={reject.note}
              onChange={e => setReject(r => ({ ...r, note: e.target.value }))}
              placeholder="Reason (optional)…"
              rows={3}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#f1f5f9', padding: '0.5rem', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setReject(null)} style={{ background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleReject} disabled={actioning === reject.id} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontWeight: 700, cursor: 'pointer' }}>
                {actioning === reject.id ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PeopleList({ people, emptyText }) {
  if (people.length === 0) return <p style={{ color: '#475569' }}>{emptyText}</p>;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '0 1rem' }}>
      {people.map((p, i) => (
        <div key={p.id || i}>
          <ContactRow
            name={p.name} email={p.email} phone={p.phone} whatsapp={p.whatsapp}
            role={p.role} active={p.active} last_login_at={p.last_login_at}
          />
          {(p.company || p.business) && (
            <div style={{ fontSize: '0.7rem', color: '#475569', margin: '-0.4rem 0 0.4rem 2.4rem' }}>
              {p.company}{p.business && p.business !== p.company ? ` · ${p.business}` : ''}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Overview() {
  const [stats,     setStats]     = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users,     setUsers]     = useState([]);
  const [pricing,   setPricing]   = useState(null);
  const [search,    setSearch]    = useState('');
  const [activeTab, setActiveTab] = useState('parents'); // parents | companies | owners | users
  const [loading,   setLoading]   = useState(true);

  const load = () => {
    setLoading(true);
    adminDashboardAPI.overview()
      .then(r => setStats(r.data))
      .catch(() => {});
    adminDashboardAPI.users()
      .then(r => setUsers(r.data.users || []))
      .catch(() => {});
    adminDashboardAPI.companies()
      .then(r => setCompanies(r.data.companies || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    adminDashboardAPI.config().then(r => setPricing(r.data.pricing)).catch(() => {});
  }, []);

  const fmt = (n) => Number(n || 0).toLocaleString();

  const parentCompanies     = companies.filter(c => c.businesses.length > 0);
  const standaloneCompanies = companies.filter(c => c.businesses.length === 0);

  const matchesSearch = (c, q) => {
    if (!q) return true;
    if (c.name.toLowerCase().includes(q)) return true;
    if (c.industry?.toLowerCase().includes(q)) return true;
    return c.businesses.some(b =>
      b.name.toLowerCase().includes(q) ||
      b.owner?.name?.toLowerCase().includes(q) ||
      b.owner?.email?.toLowerCase().includes(q) ||
      b.owner?.phone?.includes(q) ||
      b.staff.some(s => s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || s.phone?.includes(q))
    );
  };

  const q = search.toLowerCase();
  const filteredParents    = parentCompanies.filter(c => matchesSearch(c, q));
  const filteredStandalone = standaloneCompanies.filter(c => c.name.toLowerCase().includes(q) || !q);

  // Owners / users come from the authoritative /dashboard/users endpoint so the
  // lists always match the Owners and Total Users counts.
  const matchesPerson = (p) => !q
    || p.name?.toLowerCase().includes(q)
    || p.email?.toLowerCase().includes(q)
    || p.phone?.includes(q)
    || p.company?.toLowerCase().includes(q)
    || p.business?.toLowerCase().includes(q);
  const filteredUsers  = users.filter(matchesPerson);
  const filteredOwners = filteredUsers.filter(p => p.role === 'owner');

  if (loading) return <p>Loading…</p>;

  const t = stats?.totals || {};
  const totalCompanies = standaloneCompanies.length + Number(t.total_businesses || 0);

  const TABS = [
    { key: 'parents',   label: 'Parent Companies', value: parentCompanies.length, heading: 'Parent Companies' },
    { key: 'companies', label: 'Companies',         value: totalCompanies,         heading: 'Companies' },
    { key: 'owners',    label: 'Owners',            value: fmt(t.total_owners),    heading: 'Owners' },
    { key: 'users',     label: 'Total Users',       value: fmt(t.total_users),     heading: 'All Users' },
  ];
  const activeHeading = TABS.find(x => x.key === activeTab)?.heading || '';

  return (
    <div>
      <h1>Dashboard</h1>

      <UpgradeRequestsPanel />

      {/* Stat cards double as tabs — click one to reveal its data below. */}
      <div className="kpi-grid" style={{ marginBottom: '2rem' }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className="kpi-card"
              onClick={() => setActiveTab(tab.key)}
              style={{
                font: 'inherit', color: 'inherit', textAlign: 'left', width: '100%', cursor: 'pointer',
                border: `1px solid ${isActive ? '#3b82f6' : '#334155'}`,
                background: isActive ? '#243352' : '#1e293b',
                boxShadow: isActive ? '0 0 0 1px #3b82f6' : 'none',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <div className="kpi-label">{tab.label}</div>
              <div className="kpi-value">{tab.value}</div>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1rem', color: '#94a3b8', margin: 0 }}>{activeHeading}</h2>
        <input type="text" placeholder="Search names, contacts…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
      </div>

      {activeTab === 'parents' && (
        filteredParents.length > 0
          ? filteredParents.map(c => <CompanyCluster key={c.id} company={c} pricing={pricing} onRefresh={load} />)
          : <p style={{ color: '#475569' }}>No parent companies{search ? ` for "${search}"` : ''}.</p>
      )}

      {activeTab === 'companies' && (
        filteredStandalone.length > 0
          ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {filteredStandalone.map(c => <StandaloneCard key={c.id} company={c} pricing={pricing} onRefresh={load} />)}
            </div>
          )
          : <p style={{ color: '#475569' }}>No companies{search ? ` for "${search}"` : ''}.</p>
      )}

      {activeTab === 'owners' && (
        <PeopleList people={filteredOwners} emptyText={`No owners${search ? ` for "${search}"` : ''}.`} />
      )}

      {activeTab === 'users' && (
        <PeopleList people={filteredUsers} emptyText={`No users${search ? ` for "${search}"` : ''}.`} />
      )}
    </div>
  );
}
